/**
 * The arrow-key arithmetic behind the dock's composite widgets — the tablist
 * (`DockTabBar`) and the radio groups (`DockSegmented`, `DockMenuRadioGroup`).
 * Both are ONE tab stop with the arrows moving inside (WAI-ARIA APG "Tabs" and
 * "Radio Group"); they differ only in what the move does (a tab only focuses,
 * a radio also selects) and in which items the arrows may land on.
 */
export type RovingOrientation = 'horizontal' | 'vertical' | 'both';

/**
 * The index a navigation key moves to, or `null` when the key is not one this
 * widget navigates with (the caller then lets it through untouched).
 *
 * Wraps at both ends, as both APG patterns do. `canLand` excludes items the
 * arrows must skip — a radio whose own click would refuse the reader (an
 * option that is unavailable for a data reason). When nothing else can be
 * landed on, the current index comes back, so the caller still swallows the
 * key instead of letting it scroll the page.
 */
export const rovingTarget = (
  key: string,
  current: number,
  count: number,
  orientation: RovingOrientation = 'horizontal',
  canLand: (i: number) => boolean = () => true,
): number | null => {
  if (count <= 0) return null;
  const forward =
    (orientation !== 'vertical' && key === 'ArrowRight') || (orientation !== 'horizontal' && key === 'ArrowDown');
  const back =
    (orientation !== 'vertical' && key === 'ArrowLeft') || (orientation !== 'horizontal' && key === 'ArrowUp');
  const scan = (from: number, step: 1 | -1): number => {
    for (let n = 1; n <= count; n++) {
      const i = (((from + step * n) % count) + count) % count;
      if (canLand(i)) return i;
    }
    return current;
  };
  if (forward) return scan(current, 1);
  if (back) return scan(current, -1);
  // Home / End land on the first / last item that CAN be landed on.
  if (key === 'Home') return scan(-1, 1);
  if (key === 'End') return scan(count, -1);
  return null;
};
